import React, { 
  useState,
  useRef,
  useEffect,
  useCallback 
} from "react";

import 
"./OtpInput.css";

const OtpInput = ({
  length = 6,
  onChange,
  onComplete,
  hasError = false,
  autoFocus = true,
  isMasked = false,
  maskDelay = 1000,

}) => {
  const [otp, setOtp] = useState(new Array(length).fill(""));
  const [visibleIndex, setVisibleIndex] = useState(null);
  const inputRefs = useRef([]);
  const maskTimeoutRef = useRef(null);

  const clearMaskTimeout = useCallback(() => {

    if (maskTimeoutRef.current) {

      clearTimeout(maskTimeoutRef.current);
      maskTimeoutRef.current = null;
    }
  }, []);

  const handleMaskTimeout = useCallback(
    (index) => {
      clearMaskTimeout();

      const delayMs = Math.max(0, Number(maskDelay) || 0);

      if (delayMs === 0) {

        setVisibleIndex(null);
        return;
      }

      setVisibleIndex(index);
      maskTimeoutRef.current = setTimeout(() => {
        setVisibleIndex(null);
        maskTimeoutRef.current = null;
      }, delayMs);
    },
    [clearMaskTimeout, maskDelay]
  );

  useEffect(() => {
    return () => clearMaskTimeout();

  }, [clearMaskTimeout]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {

      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const getDisplayValue = (index) => {
    if (!isMasked) {

      return otp[index];
    }

    if (visibleIndex === index) {

      return otp[index];
    }

    return otp[index] ? "•" : "";
  };

  const handleChange = (e, index) => {
    const value = e.target.value;

    const cleanValue = value.replace(/•/g, "");
    if (!/^\d*$/.test(cleanValue)) return;

    const newOtp = [...otp];
    newOtp[index] = cleanValue.slice(-1);
    setOtp(newOtp);

    const otpString = newOtp.join("");
    if (onChange) onChange(otpString);

    if (isMasked && cleanValue) {

      handleMaskTimeout(index);
    }

    if (cleanValue && index < length - 1) {

      inputRefs.current[index + 1].focus();
    }

    if (newOtp.every((digit) => digit !== "") && onComplete) {

      onComplete(otpString);
    }
  };

  const handleKeyDown = (e, index) => {

    if (e.key === "Backspace") {

      if (!otp[index] && index > 0) {

        inputRefs.current[index - 1].focus();
      }

      setVisibleIndex(null);

    } else if (e.key === "ArrowLeft" && index > 0) {

      inputRefs.current[index - 1].focus();

    } else if (e.key === "ArrowRight" && index < length - 1) {

      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, length);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split("").forEach((char, i) => {
      if (i < length) newOtp[i] = char;

    });
    setOtp(newOtp);

    const otpString = newOtp.join("");
    if (onChange) onChange(otpString);

    const nextEmptyIndex = newOtp.findIndex((digit) => digit === "");
    const focusIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
    inputRefs.current[focusIndex].focus();

    setVisibleIndex(null);

    if (newOtp.every((digit) => digit !== "") && onComplete) {

      onComplete(otpString);
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  return (
    <div className="otp-input-container">
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={getDisplayValue(index)}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          onFocus={handleFocus}
          className={`otp-input-box ${hasError ? "otp-input-error" : ""}`}
          autoComplete="one-time-code"
          aria-label={`OTP digit ${index + 1}`}
        />
      ))}
    </div>
  );
};

export default OtpInput;
